import React, { useState, useEffect } from 'react';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase';
import { sendPasswordResetEmail, deleteUser } from 'firebase/auth';
import { collection, getDocs, setDoc, doc, deleteDoc, query, where } from 'firebase/firestore';

interface Category {
  id: string;
  name: string;
  color: string;
}

interface LearningLogType {
  id: string;
  name: string;
  description: string;
}

const defaultLearningLogTypes: LearningLogType[] = [
  { id: '1', name: 'Course', description: 'Structured learning program or course' },
  { id: '2', name: 'Tutorial', description: 'Step-by-step learning guide' },
  { id: '3', name: 'Workshop', description: 'Hands-on learning session' },
  { id: '4', name: 'Reading', description: 'Book, article, or documentation' },
  { id: '5', name: 'Practice', description: 'Practical application or exercise' },
  { id: '6', name: 'Project', description: 'Learning through project work' },
];

const colorOptions = [
  'bg-blue-500',
  'bg-green-500',
  'bg-yellow-500',
  'bg-red-500',
  'bg-purple-500',
  'bg-pink-500',
  'bg-indigo-500',
  'bg-gray-500',
];

export default function Settings() {
  const { logout, currentUser } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState<Category[]>([]);
  const [learningLogTypes, setLearningLogTypes] = useState<LearningLogType[]>([]);
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [showNewType, setShowNewType] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [editingType, setEditingType] = useState<LearningLogType | null>(null);
  const [newCategory, setNewCategory] = useState<Partial<Category>>({
    name: '',
    color: colorOptions[0]
  });
  const [newType, setNewType] = useState<Partial<LearningLogType>>({
    name: '',
    description: ''
  });

  useEffect(() => {
    if (!currentUser) return;
    const fetchData = async () => {
      const categoriesRef = collection(db, 'learningLogCategories');
      const typesRef = collection(db, 'learningLogTypes');
      const categoriesQ = query(categoriesRef, where('uid', '==', currentUser.uid));
      const typesQ = query(typesRef, where('uid', '==', currentUser.uid));
      const [categoriesSnap, typesSnap] = await Promise.all([
        getDocs(categoriesQ),
        getDocs(typesQ)
      ]);
      setCategories(categoriesSnap.docs.map(doc => doc.data() as Category));
      if (typesSnap.empty) {
        // If no types, set defaults for this user
        const defaultTypesWithUid = defaultLearningLogTypes.map(type => ({ ...type, uid: currentUser.uid }));
        await Promise.all(defaultTypesWithUid.map(type => setDoc(doc(db, 'learningLogTypes', type.id), type)));
        setLearningLogTypes(defaultTypesWithUid);
      } else {
        setLearningLogTypes(typesSnap.docs.map(doc => doc.data() as LearningLogType));
      }
    };
    fetchData();
  }, [currentUser]);

  const handleSaveCategory = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!newCategory.name || !newCategory.color || !currentUser) return;
    const category: Category & { uid: string } = {
      id: editingCategory?.id || Date.now().toString(),
      name: newCategory.name,
      color: newCategory.color,
      uid: currentUser.uid,
    };
    try {
      await setDoc(doc(db, 'learningLogCategories', category.id), category);
      if (editingCategory) {
        setCategories(categories.map(c => c.id === category.id ? category : c));
      } else {
        setCategories([...categories, category]);
      }
      setShowNewCategory(false);
      setEditingCategory(null);
      setNewCategory({ name: '', color: colorOptions[0] });
    } catch (err) {
      alert('Failed to save category.');
    }
  };

  const handleSaveType = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!newType.name || !newType.description || !currentUser) return;
    const type: LearningLogType & { uid: string } = {
      id: editingType?.id || Date.now().toString(),
      name: newType.name,
      description: newType.description,
      uid: currentUser.uid,
    };
    try {
      await setDoc(doc(db, 'learningLogTypes', type.id), type);
      if (editingType) {
        setLearningLogTypes(types => types.map(t => t.id === type.id ? type : t));
      } else {
        setLearningLogTypes(types => [...types, type]);
      }
      setShowNewType(false);
      setEditingType(null);
      setNewType({ name: '', description: '' });
    } catch (err) {
      alert('Failed to save type.');
    }
  };

  const handleDeleteCategory = async (categoryId: string) => {
    if (window.confirm('Are you sure you want to delete this category? All entries in this category will be moved to "Other".')) {
      try {
        await deleteDoc(doc(db, 'learningLogCategories', categoryId));
        setCategories(categories.filter(c => c.id !== categoryId));
        // Optionally, update entries in Firestore to move them to "Other"
      } catch (err) {
        alert('Failed to delete category.');
      }
    }
  };

  const handleDeleteType = async (typeId: string) => {
    if (window.confirm('Are you sure you want to delete this learning log type? This action cannot be undone.')) {
      try {
        await deleteDoc(doc(db, 'learningLogTypes', typeId));
        setLearningLogTypes(types => types.filter(t => t.id !== typeId));
      } catch (err) {
        alert('Failed to delete type.');
      }
    }
  };

  if (!currentUser) return <div>Loading...</div>;

  return (
    <div className="max-w-4xl mx-auto p-4 space-y-8">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold">Settings</h1>
      </div>

      {/* Categories Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">Course Categories</h2>
          <button
            onClick={() => setShowNewCategory(true)}
            className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Add Category
          </button>
        </div>

        <div className="space-y-4">
          {categories.map(category => (
            <div key={category.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div className="flex items-center space-x-3">
                <div className={`w-4 h-4 rounded-full ${category.color}`} />
                <span className="font-medium">{category.name}</span>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    setEditingCategory(category);
                    setNewCategory(category);
                    setShowNewCategory(true);
                  }}
                  className="p-1 text-gray-500 hover:text-blue-500"
                >
                  <PencilIcon className="w-5 h-5" />
                </button>
                <button
                  onClick={() => handleDeleteCategory(category.id)}
                  className="p-1 text-gray-500 hover:text-red-500"
                >
                  <TrashIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Learning Log Types Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-soft p-6">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold">Learning Log Types</h2>
          <button
            onClick={() => setShowNewType(true)}
            className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Add Type
          </button>
        </div>

        <div className="space-y-4">
          {learningLogTypes.map(type => (
            <div key={type.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div>
                <h3 className="font-medium">{type.name}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">{type.description}</p>
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    setEditingType(type);
                    setNewType(type);
                    setShowNewType(true);
                  }}
                  className="p-1 text-gray-500 hover:text-blue-500"
                >
                  <PencilIcon className="w-5 h-5" />
                </button>
                <button
                  onClick={() => handleDeleteType(type.id)}
                  className="p-1 text-gray-500 hover:text-red-500"
                >
                  <TrashIcon className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* New/Edit Category Modal */}
      {showNewCategory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <form onSubmit={handleSaveCategory} className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {editingCategory ? 'Edit Category' : 'New Category'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={newCategory.name}
                  onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Color
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {colorOptions.map(color => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setNewCategory({ ...newCategory, color })}
                      className={`w-full h-8 rounded-md ${color} ${
                        newCategory.color === color ? 'ring-2 ring-offset-2 ring-blue-500' : ''
                      }`}
                    />
                  ))}
                </div>
              </div>

              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowNewCategory(false);
                    setEditingCategory(null);
                    setNewCategory({ name: '', color: colorOptions[0] });
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                  {editingCategory ? 'Save Changes' : 'Add Category'}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {/* New/Edit Type Modal */}
      {showNewType && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <form onSubmit={handleSaveType} className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {editingType ? 'Edit Type' : 'New Learning Log Type'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name
                </label>
                <input
                  type="text"
                  value={newType.name}
                  onChange={(e) => setNewType({ ...newType, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={newType.description}
                  onChange={(e) => setNewType({ ...newType, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  required
                />
              </div>

              <div className="flex justify-end space-x-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowNewType(false);
                    setEditingType(null);
                    setNewType({ name: '', description: '' });
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                  {editingType ? 'Save Changes' : 'Add Type'}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}
    </div>
  );
} 